import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { Film } from '../api/film';
import { useBatchUsersQuery } from '../hooks/useBatchUsersQuery';

/**
 * 每个预算的数据缓存
 */
interface LocalBudgetItem {
  budgetItemID: string;
  budgetItemCode: string;
  budgetItemName: string;
  budgetItemBelongBudgetID: string;
  budgetItemParentID: string | null;
  budgetItemAmount: number;
  budgetItemUsedAmount: number;
  budgetItemRemainingAmount: number;
  budgetItemProgressType: string;
  budgetItemCurrency?: string;
  budgetItemUnit1?: string | null;
  budgetItemUnit2?: string | null;
  budgetItemUnitPrice?: number;
  budgetItemQuantity1?: number;
  budgetItemQuantity2?: number;
  budgetItemRelatedID: string | null;
  budgetItemAssigneeID: string | null;
  budgetItemPartners: string[];
  budgetItemRemark: string;
  budgetItemFilmCode: string | null;
  budgetItemCreateTime: string;
  budgetItemUpdateTime: string;
  budgetItemLevel: number;
  budgetItemRole?: string;
  budgetItemOwnerID?: string;
  budgetItemCreatorID?: string;
  budgetItemUnusedAmount?: number;
  budgetItemExceedingAmount?: number;
  budgetItemStatus?: string;
  budgetItemDataStatus?: string;
  budgetItemChildren?: LocalBudgetItem[];
  budgetItemExpanded?: boolean;
  budgetItemChecked?: boolean;
  isRelatedItem?: boolean;
  isRelatedContainer?: boolean;
  relatedParentID?: string;
  calculatedRelatedAmount?: number;
}

interface BudgetDataCache {
  budgetName: string;
  budgetID: string | null;
  budgetOwnerID?: string; // 用于收集用户ID
  totalAmount: number;
  budgetItems: LocalBudgetItem[];
  budgetStatus: string;
  activeCategory: string;
  totalAmountCurrency: string;
  budgetMainCurrency: string;
  isAllExpanded: boolean;
  currentFilm: Film | null;
  filmBudgets: any[];
  assigneeName: string;
}

interface BudgetDataCacheContextType {
  // 获取缓存
  getCache: (budgetId: string) => BudgetDataCache | undefined;
  // 设置缓存
  setCache: (budgetId: string, data: Partial<BudgetDataCache>) => void;
  // 订阅缓存变化
  subscribeBudgetCache: (budgetId: string, callback: () => void) => () => void;
  // 清除缓存
  clearCache: (budgetId: string) => void;
  // 清除所有缓存
  clearAllCache: () => void;
  // 检查缓存是否存在
  hasCache: (budgetId: string) => boolean;
}

const BudgetDataCacheContext = createContext<BudgetDataCacheContextType | undefined>(undefined);

export const useBudgetDataCache = () => {
  const context = useContext(BudgetDataCacheContext);
  if (context === undefined) {
    throw new Error('useBudgetDataCache must be used within a BudgetDataCacheProvider');
  }
  return context;
};

export const BudgetDataCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 使用 ref 存储缓存，避免触发重新渲染
  const cacheRef = useRef<Map<string, BudgetDataCache>>(new Map());
  // 订阅者存储
  const listenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  // BroadcastChannel 用于跨标签页同步
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const { collectAndQueryUsers } = useBatchUsersQuery();

  // 通用的通知订阅者函数
  const notifyListeners = useCallback((budgetId: string) => {
    const listeners = listenersRef.current.get(budgetId);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error('[BudgetDataCache] 订阅者回调执行失败:', error);
        }
      });
    }
  }, []);

  // 初始化 BroadcastChannel
  useEffect(() => {
    // 检查浏览器是否支持 BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        broadcastChannelRef.current = new BroadcastChannel('lineup-budget-cache');

        // 监听其他标签页的消息
        broadcastChannelRef.current.onmessage = (event: MessageEvent<{ type: string; cacheType: string; id: string; action: string; timestamp: number }>) => {
          const message = event.data;
          if (message.type === 'CACHE_UPDATE' && message.cacheType === 'budget') {
            // 通知订阅者
            notifyListeners(message.id);
          }
        };
      } catch (error) {
        console.warn('[BudgetDataCache] BroadcastChannel 初始化失败，跨标签页同步将不可用:', error);
      }
    } else {
      console.warn('[BudgetDataCache] 浏览器不支持 BroadcastChannel，跨标签页同步将不可用');
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [notifyListeners]);

  // 广播消息函数
  const broadcastMessage = useCallback((budgetId: string, action: 'update' | 'delete' | 'create' | 'refresh' = 'update') => {
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({
          type: 'CACHE_UPDATE',
          cacheType: 'budget',
          id: budgetId,
          action,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error('[BudgetDataCache] 发送跨标签页消息失败:', error);
      }
    }
  }, []);

  const getCache = useCallback((budgetId: string): BudgetDataCache | undefined => {
    return cacheRef.current.get(budgetId);
  }, []);

  const setCache = useCallback((budgetId: string, data: Partial<BudgetDataCache>) => {
    const existingCache = cacheRef.current.get(budgetId);
    const isNewCache = !existingCache;
    
    if (existingCache) {
      // 合并现有缓存
      cacheRef.current.set(budgetId, { ...existingCache, ...data });
    } else {
      // 创建新缓存
      const defaultCache: BudgetDataCache = {
        budgetName: '未命名',
        budgetID: null,
        totalAmount: 0,
        budgetItems: [],
        budgetStatus: '预算中',
        activeCategory: 'allbudget',
        totalAmountCurrency: 'RMB',
        budgetMainCurrency: 'RMB',
        isAllExpanded: true,
        currentFilm: null,
        filmBudgets: [],
        assigneeName: '',
        ...data,
      };
      cacheRef.current.set(budgetId, defaultCache);
    }

    // 通知本标签页的订阅者
    notifyListeners(budgetId);

    // 广播到其他标签页
    broadcastMessage(budgetId, 'update');

    // 如果是新缓存，异步批量查询用户信息（不阻塞页面加载）
    if (isNewCache) {
      const finalCache = cacheRef.current.get(budgetId);
      if (finalCache) {
        // 收集资源ID用于权限查询
        const resourceIDs: string[] = [];
        if (finalCache.budgetID) {
          resourceIDs.push(finalCache.budgetID);
        }
        if (finalCache.budgetItems && finalCache.budgetItems.length > 0) {
          finalCache.budgetItems.forEach(item => {
            if (item.budgetItemID) {
              resourceIDs.push(item.budgetItemID);
            }
          });
        }
        
        // 异步批量查询用户信息（不阻塞页面加载）
        collectAndQueryUsers(
          {
            budgetOwnerID: finalCache.budgetOwnerID,
            budgetItems: finalCache.budgetItems || [],
          },
          'Budget',
          resourceIDs
        ).catch(error => {
          console.warn('批量查询预算相关用户信息失败（不影响主流程）:', error);
        });
      }
    }
  }, [collectAndQueryUsers, notifyListeners, broadcastMessage]);

  const subscribeBudgetCache = useCallback((budgetId: string, callback: () => void) => {
    if (!listenersRef.current.has(budgetId)) {
      listenersRef.current.set(budgetId, new Set());
    }
    listenersRef.current.get(budgetId)!.add(callback);

    // 返回取消订阅函数
    return () => {
      listenersRef.current.get(budgetId)?.delete(callback);
    };
  }, []);

  const clearCache = useCallback((budgetId: string) => {
    cacheRef.current.delete(budgetId);
    listenersRef.current.delete(budgetId);
  }, []);

  const clearAllCache = useCallback(() => {
    cacheRef.current.clear();
    listenersRef.current.clear();
  }, []);

  const hasCache = useCallback((budgetId: string): boolean => {
    return cacheRef.current.has(budgetId);
  }, []);

  const value: BudgetDataCacheContextType = {
    getCache,
    setCache,
    subscribeBudgetCache,
    clearCache,
    clearAllCache,
    hasCache,
  };

  return (
    <BudgetDataCacheContext.Provider value={value}>
      {children}
    </BudgetDataCacheContext.Provider>
  );
};


